import {
  useGetOne,
  useGetResourceLabel,
  useResourceContext,
  useTranslate,
} from 'ra-core';
import { useParams } from 'react-router-dom';
import { MoonstarList } from '../Moonstar/MoonstarList';
import { MoonstarListActions } from '../Moonstar/MoonstarListActions';
import { createBasicUserFilters } from '../User/UserFilters';
import { useIsSmallScreen } from '../../hooks/useIsSmallScreen';
import { UserPictureField } from '../User/UserPictureField';
import { StringArrayBadgeField } from '../User/StringArrayBadgeField';
import { type FC, useMemo } from 'react';
import { ListExportButton } from '../../components/custom/ListExportButton';
import { createActivityAnalyticsListExporter } from './createActivityAnalyticsListExporter';
import { DataTable } from '../../components/admin/data-table';
import { DateField } from '../../components/admin/date-field';
import { SelectField } from '../../components/admin/select-field';
import { SelectInput } from '../../components/admin/select-input';

const ACTIVITY_STATUS_CHOICES = [
  {
    label: 'moonstar.journeys.activityStatus.notStarted',
    value: 'not-started',
  },
  { label: 'moonstar.journeys.activityStatus.opened', value: 'opened' },
  { label: 'moonstar.journeys.activityStatus.done', value: 'done' },
];

export const ActivityAnalytics: FC = () => {
  const { journeyId, sectionId, activityId } = useParams<{
    journeyId: string;
    sectionId: string;
    activityId: string;
  }>();
  const resource = useResourceContext();
  const journey = useGetOne<{
    id: string;
    name: string;
    sections: [
      { id: string; name: string; activities: [{ id: string; name: string }] },
    ];
  }>(resource!, { id: journeyId });
  const getLabel = useGetResourceLabel();
  const translate = useTranslate();

  const [journeyName, sectionName, activityName] = useMemo(() => {
    if (!journey.data) {
      return [`# ${journeyId}`, `# ${sectionId}`, `# ${activityId}`];
    }

    const journeyName = journey.data.name;
    const section = journey.data.sections.find((s) => s.id === sectionId);
    const sectionName = section ? section.name : `# ${sectionId}`;
    const activity = section?.activities.find((a) => a.id === activityId);
    const activityName = activity ? activity.name : `# ${activityId}`;

    return [journeyName, sectionName, activityName] as const;
  }, [journey.data, activityId, sectionId, journeyId]);
  const isSmallScreen = useIsSmallScreen();
  const userFilters = useMemo(
    () => createBasicUserFilters({ isSmallScreen }),
    [isSmallScreen]
  );

  return (
    <MoonstarList
      filters={[
        <SelectInput
          choices={ACTIVITY_STATUS_CHOICES}
          optionText="label"
          optionValue="value"
          source="activityStatus"
          alwaysOn
        />,
        ...userFilters,
      ]}
      filter={{
        userIsDisabled: false,
      }}
      actions={
        <MoonstarListActions
          hideCreate
          right={<ListExportButton maxResults={Infinity} />}
        />
      }
      sort={{ field: 'activityStatus', order: 'DESC' }}
      resource={`journeys/${journeyId}/sections/${sectionId}/activities/${activityId}/analytics`}
      title={[
        getLabel(resource!, 1),
        journeyName,
        sectionName,
        activityName,
        translate('moonstar.analytics.name'),
      ]}
      exporter={createActivityAnalyticsListExporter({
        journeyName: journeyName,
        sectionName: sectionName,
        activityName: activityName,
      })}
    >
      <DataTable rowClick={false}>
        <DataTable.Col source="pictureUrl" disableSort>
          <UserPictureField />
        </DataTable.Col>
        <DataTable.Col
          source="internalId"
          label="resources.users.fields.internalId"
        />
        <DataTable.Col source="firstName" />
        <DataTable.Col source="lastName" />
        <DataTable.Col source="phoneNumber" />
        <DataTable.Col source="email" />
        <DataTable.Col source="jobTitleName" />
        <DataTable.Col source="departmentName" />
        <DataTable.Col source="regionName" />
        <DataTable.Col source="units" disableSort>
          <StringArrayBadgeField source="units" />
        </DataTable.Col>
        <DataTable.Col
          source="activityStatus"
          label="resources.journeys.fields.activityStatus"
        >
          <SelectField
            source="activityStatus"
            optionText="label"
            optionValue="value"
            choices={ACTIVITY_STATUS_CHOICES}
          />
        </DataTable.Col>
        <DataTable.Col
          source="activityStartedAt"
          label="resources.journeys.fields.activityStartedAt"
          disableSort
        >
          <DateField
            source="activityStartedAt"
            showTime
            empty="moonstar.never"
          />
        </DataTable.Col>
        <DataTable.Col
          source="activityMarkedAsDoneAt"
          label="resources.journeys.fields.activityMarkedAsDoneAt"
          disableSort
        >
          <DateField
            source="activityMarkedAsDoneAt"
            showTime
            empty="moonstar.never"
          />
        </DataTable.Col>
      </DataTable>
    </MoonstarList>
  );
};
